import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import EvervizEmbed from '../../components/EvervizEmbed'
import RichText from '../../components/RichText'
import Modal from '../../components/Modal'
import Charts from '../../components/Charts'
import { ISection, IBuild } from '../../@customTypes/generated/contentful'
import { eventTracking } from '../../utils/Tracking'
import styles from './NavAppTemplate.module.css'

interface ComponentProps {
  sectionId: string
  navAppIndex?: number
}

function NavAppTemplate({ sectionId, navAppIndex = 0 }: ComponentProps) {
  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  )
  const [showModal, setShowModal] = useState<boolean | null>(null)
  const [section, setSection] = useState<ISection | null>(null)
  const sectionEndpoint = `${process.env.PUBLIC_URL}/data/sections/${sectionId}.json`
  const navigate = useNavigate()

  useEffect(() => {
    global.document.body.style.overflowY = showModal ? 'hidden' : 'auto'
    if (showModal === false) {
      navigate(window.location.pathname, { replace: true })
    }
  }, [showModal, navigate])

  useEffect(() => {
    if (
      section?.fields.slug === searchParams.get('sectionSlug') &&
      showModal === null
    ) {
      setShowModal(true)
    }
  }, [section, searchParams, showModal])

  useEffect(() => {
    const getEntries = async () => {
      console.log('FETCH ENTRIES')
      const response = await fetch(sectionEndpoint)
      const items = await response.json()
      if (items[0]) {
        setSection(items[0])
      }
    }
    if (
      window.snapStore &&
      window.snapStoreStore[navAppIndex][sectionEndpoint]
    ) {
      setSection(window.snapStoreStore[navAppIndex][sectionEndpoint][0])
    } else {
      getEntries()
    }
  }, [sectionId, sectionEndpoint, navAppIndex])

  let builds = section?.fields?.builds
  let modalBuilds

  if (showModal && section?.fields?.builds?.[0]) {
    builds = section?.fields?.builds?.slice(
      0,
      -section?.fields?.builds.length + 1
    )
    modalBuilds = section?.fields?.builds?.slice(1)
  }

  return (
    <div
      className={`eo-nav-app-template ${styles['nav-app-template']}`}
      data-section-id={sectionId}
    >
      <div className={styles['copy-container']}>
        {section?.fields.displayTitle?.content?.[0] ? (
          <div className={styles['section-title-rich']}>
            <RichText content={section?.fields.displayTitle} makeH2 />
          </div>
        ) : (
          <h2 className={styles['section-title']}>{section?.fields.title}</h2>
        )}
      </div>
      <div>
        {builds?.map(({ fields, sys }: IBuild, buildIndex: number) => (
          <div
            key={sys.id}
            className={buildIndex > 0 ? styles['hidden-off-screen'] : ''}
          >
            <div
              className={
                fields.hasFullBleedCharts ? '' : styles['copy-container']
              }
            >
              <div className={styles.charts}>
                <Charts
                  charts={fields.charts}
                  buildId={sys.id}
                  sectionTitle={section?.fields.title}
                  embedCharts={!searchParams.get('hideCharts')}
                />
              </div>
            </div>

            <div className={styles['copy-container']}>
              {buildIndex === 0 && !!fields.bodyText && (
                <div className={styles['section-promo-text']}>
                  <RichText content={fields.bodyText} />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {showModal && modalBuilds && (
        <Modal setShowModal={setShowModal} sectionTitle={section?.fields.title}>
          <div className={styles['modal-container']}>
            <h2 className={styles['section-title']}>{section?.fields.title}</h2>
            {section?.fields.showFirstBuildsBodyTextInModal &&
              !!builds?.[0]?.fields.bodyText && (
                <div className={styles['section-promo-text']}>
                  <RichText content={builds?.[0]?.fields.bodyText} />
                </div>
              )}
            {modalBuilds?.map(({ fields, sys }: IBuild) => (
              <div key={sys.id} className={styles['modal-build']}>
                <div>
                  <div
                    className={styles.charts}
                    style={{
                      gridTemplateColumns: new Array(fields.charts?.length ?? 0)
                        .fill('1fr')
                        .join(' '),
                    }}
                  >
                    {fields.charts?.map((chart, index) => (
                      <EvervizEmbed
                        key={`${index}${sys.id}${chart.fields.embedId}`}
                        uid={chart.fields.embedId}
                        isLayout={chart.fields.isLayout}
                        chartTitle={chart.fields.title}
                        sectionTitle={section?.fields.title}
                        embedCharts={!searchParams.get('hideCharts')}
                      />
                    ))}
                  </div>
                </div>

                <RichText content={fields.bodyText} />
                <div className={styles.footnote}>
                  <RichText content={fields.footnote} />
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}
      <div className={styles['copy-container']}>
        <button
          className={styles['read-more']}
          onClick={() => {
            eventTracking({
              event: 'bp-global-energy-outlook-interaction',
              eventAction: 'click',
              eventCategory: section?.fields.title,
              eventLabel: 'Modal - open',
              eventUrl: window.location.href,
            })
            navigate(
              `${window.location.pathname}?sectionSlug=${section?.fields.slug}`,
              { replace: true }
            )
            setShowModal(true)
          }}
        >
          Read more
        </button>
      </div>
    </div>
  )
}

export default NavAppTemplate
